import { FormControl } from '@angular/forms';
import { BookableStorageLocation } from '@luggagehero/shared/interfaces';

export interface ProviderUrl {
  providerName: string;
  urlPattern: RegExp;
}

/**
 * Returns the best available compose-ready review URL for a shop.
 *
 * Precedence:
 *   1. `externalReviewUrl` (manually entered) — staff override. If set, this wins so ops can
 *      deliberately steer reviews to a different platform than the auto-synced one.
 *   2. `externalBusinessProfiles.gbp.reviewUrl` (synced from GBP `metadata.newReviewUri`) —
 *      the default, auto-maintained for any shop with a verified GBP profile.
 *
 * When future providers (Apple Maps, TripAdvisor, etc.) come online, extend the fallback
 * chain here. Manual entries that exist for historic reasons (not as overrides) should be
 * audited and cleared before this ships, otherwise they will silently win over fresh GBP
 * data.
 */
export function getEffectiveExternalReviewUrl(
  shop: Pick<BookableStorageLocation, 'externalReviewUrl' | 'externalBusinessProfiles'> | null | undefined,
): string {
  if (!shop) {
    return '';
  }
  return shop.externalReviewUrl || shop.externalBusinessProfiles?.gbp?.reviewUrl || '';
}

export const EXTERNAL_REVIEW_PLATFORMS: ProviderUrl[] = [
  { urlPattern: /^https:\/\/search.google.com\/local\/writereview/, providerName: 'Google' },
  { urlPattern: /^https:\/\/g.page\/r\/..*\/review/, providerName: 'Google' },
  { urlPattern: /^https:\/\/(www.)?tripadvisor.com\/UserReviewEdit/, providerName: 'TripAdvisor' },
  { urlPattern: /^https:\/\/(www.)?facebook.com\/..*\/reviews(\/)?$/, providerName: 'Facebook' },
  { urlPattern: /^https:\/\/(www.)?yelp.com\/writeareview\/biz\//, providerName: 'Yelp' },
];

export const GOOGLE_MAPS_EMBED_URL_MATCHER: ProviderUrl = {
  urlPattern: /^https:\/\/(www.)?google.com\/maps\/embed/,
  providerName: 'Google',
};

export class UrlValidators {
  static googleMapsEmbedUrl(control: FormControl) {
    if (!control.value) {
      return null;
    }
    const url = control.value as string;
    if (GOOGLE_MAPS_EMBED_URL_MATCHER.urlPattern.test(url)) {
      return null;
    }
    return { invalidUrl: true };
  }

  static externalReviewUrl(control: FormControl) {
    if (!control.value) {
      return null;
    }
    const url = control.value as string;
    for (let i = 0; i < EXTERNAL_REVIEW_PLATFORMS.length; i++) {
      const provider = EXTERNAL_REVIEW_PLATFORMS[i];
      if (provider.urlPattern.test(url)) {
        // Matching provider found
        return null;
      }
    }
    return { invalidUrl: true };
  }
}
